import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useUsers } from './UsersContext';
import { fetchAllDonations, donationAction, fetchAllExpenses, createExpense, expenseAction, createDonation } from '../services/donationService';
import { fetchConfig } from '../services/configService';
import { T, Th, Tr, ActionBtn, Modal, UserDetailFields, TableCard, TableBar, SearchInput, Toast, RejectModal, PrimaryBtn, getStatusClass } from './theme';
import { useRefresh } from './RefreshContext';

const FALLBACK_EXPENSE_CATEGORIES = ['Maintenance', 'Event', 'Travel', 'Utilities', 'Pooja', 'Other'];
const FALLBACK_PAYMENT_METHODS = ['UPI', 'Cash', 'Bank Transfer', 'Cheque'];

const TransactionsPage = () => {
  const { role } = useAuth();
  const { getUserName, getUserObj } = useUsers();
  const { refresh, triggerRefresh } = useRefresh();
  const [activeTab, setActiveTab] = useState('donations');
  const [donations, setDonations] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [expenseForm, setExpenseForm] = useState({ amount: '', purpose: '', paid_to: '', expense_date: '', paymentMethod: 'UPI', category: 'Other' });
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState(FALLBACK_EXPENSE_CATEGORIES);
  const [paymentMethods, setPaymentMethods] = useState(FALLBACK_PAYMENT_METHODS);
  const [donationTypes, setDonationTypes] = useState([]);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [donationForm, setDonationForm] = useState({ donorContact: '', donorName: '', donationAmount: '', donationType: '', donationDate: '' });
  const [viewUserModal, setViewUserModal] = useState(null);
  const [viewReview, setViewReview] = useState(null);

  const roles = (role || '').split(',').map(s => s.trim().toUpperCase());
  const isAdmin = roles.includes('ADMIN');
  const userEmail = localStorage.getItem('vrvEmail') || '';

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const loadData = async () => {
    setLoading(true);
    const [donData, expData] = await Promise.all([fetchAllDonations(), fetchAllExpenses()]);
    donData.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    expData.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    setDonations(donData); setExpenses(expData);
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    fetchConfig('EXPENSE_CATEGORY').then(cats => { if (cats.length) setExpenseCategories(cats); });
    fetchConfig('PAYMENT_METHOD').then(methods => { if (methods.length) setPaymentMethods(methods); });
    fetchConfig('DONATION_TYPE').then(types => { if (types.length) { setDonationTypes(types); setDonationForm(prev => ({ ...prev, donationType: types[0] })); } });
  }, [refresh]);

  const handleAdminDonationSubmit = async (e) => {
    e.preventDefault();
    if (!donationForm.donorContact || !donationForm.donationAmount) { showMsg('Donor contact and amount are required', 'error'); return; }
    const recordedBy = userEmail;
    const reviewerFullName = `${localStorage.getItem('vrvName') || ''} ${localStorage.getItem('vrvSurname') || ''}`.trim() || recordedBy;
    const res = await createDonation({ emailID: donationForm.donorContact, donorName: donationForm.donorName || donationForm.donorContact, donationAmount: donationForm.donationAmount, donationType: donationForm.donationType, recordedBy, status: 'APPROVED', reviewedBy: recordedBy, reviewerFullName, reviewedAt: new Date().toISOString(), reviewComments: 'Auto-approved by admin', createdAt: donationForm.donationDate ? new Date(donationForm.donationDate).toISOString() : new Date().toISOString() });
    if (res.statusCode === 201) { showMsg('Donation recorded & approved!', 'success'); setShowDonationForm(false); setDonationForm({ donorContact: '', donorName: '', donationAmount: '', donationType: donationTypes[0] || '', donationDate: '' }); loadData(); triggerRefresh(); }
    else showMsg(res.message || 'Failed', 'error');
  };

  const handleDonationAction = async (action, comments = '') => {
    if (selectedIds.length === 0) { showMsg('Select at least one item', 'error'); return; }
    if (action === 'REJECT' && !comments) { setRejectModal(true); return; }
    const reviewerEmail = userEmail;
    const reviewerFullName = `${localStorage.getItem('vrvName') || ''} ${localStorage.getItem('vrvSurname') || ''}`.trim() || reviewerEmail;
    let allSuccess = true;
    for (const donationID of selectedIds) {
      const payload = { donationID, action };
      if (action === 'APPROVE') { payload.reviewedBy = reviewerEmail; payload.reviewerFullName = reviewerFullName; payload.reviewedAt = new Date().toISOString(); payload.reviewComments = 'APPROVED'; }
      if (action === 'REJECT') { payload.reviewedBy = reviewerEmail; payload.reviewerFullName = reviewerFullName; payload.reviewedAt = new Date().toISOString(); payload.reviewComments = comments; }
      const res = await donationAction(payload);
      if (res.statusCode !== 200) allSuccess = false;
    }
    showMsg(allSuccess ? `${action} applied to ${selectedIds.length} item(s)` : 'Some actions failed', allSuccess ? 'success' : 'error');
    setSelectedIds([]); loadData(); triggerRefresh();
  };

  const handleExpenseSubmit = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || !expenseForm.purpose) { showMsg('Amount and purpose are required', 'error'); return; }
    if (editExpense) {
      const res = await expenseAction({ expense_id: editExpense.expense_id, action: 'UPDATE', ...expenseForm });
      if (res.statusCode === 200) { showMsg('Expense updated!', 'success'); setEditExpense(null); setShowExpenseForm(false); setExpenseForm({ amount: '', purpose: '', paid_to: '', expense_date: '', paymentMethod: 'UPI', category: 'Other' }); loadData(); triggerRefresh(); }
      else showMsg('Update failed', 'error');
    } else {
      const res = await createExpense({ ...expenseForm, createdBy: userEmail, createdAt: new Date().toISOString() });
      if (res.statusCode === 201) { showMsg('Expense recorded!', 'success'); setShowExpenseForm(false); setExpenseForm({ amount: '', purpose: '', paid_to: '', expense_date: '', paymentMethod: 'UPI', category: 'Other' }); loadData(); triggerRefresh(); }
      else showMsg(res.message || 'Failed', 'error');
    }
  };

  const handleEditExpense = (exp) => {
    setEditExpense(exp);
    setExpenseForm({ amount: exp.amount || '', purpose: exp.purpose || '', paid_to: exp.paid_to || '', expense_date: exp.expense_date || '', paymentMethod: exp.paymentMethod || 'UPI', category: exp.category || 'Other' });
    setShowExpenseForm(true);
  };

  const filteredDonations = donations.filter(d => search === '' || Object.values(d).some(val => String(val).toLowerCase().includes(search.toLowerCase())));
  const filteredExpenses = expenses.filter(e => search === '' || Object.values(e).some(val => String(val).toLowerCase().includes(search.toLowerCase())));
  const toggleSelect = id => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedIds(selectedIds.length === filteredDonations.length ? [] : filteredDonations.map(d => d.donationID));

  const totalDonations = donations.filter(d => d.status === 'APPROVED').reduce((s, d) => s + Number(d.donationAmount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);

  const Field = ({ label, children }) => (
    <div>
      <label className="vrv-label">{label}</label>
      {children}
    </div>
  );

  if (!isAdmin) return (
    <div className="py-20 text-center rounded-2xl" style={{ border: '1px solid rgba(0,0,0,0.07)' }}>
      <p className="font-black uppercase tracking-widest text-sm" style={{ color: T.ink }}>Access Denied</p>
      <p className="text-sm mt-2" style={{ color: T.muted }}>Only admins can access this page.</p>
    </div>
  );

  return (
    <div>
      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-2xl" style={{ background: '#F1EDE4', border: '1px solid rgba(0,0,0,0.07)' }}>
        {[['donations', 'Manage Donations'], ['expenses', 'Manage Expenses']].map(([id, label]) => (
          <button key={id} onClick={() => { setActiveTab(id); setSelectedIds([]); setSearch(''); }}
            className="px-5 py-2.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all"
            style={activeTab === id ? { background: T.navy, color: 'white', borderRadius: '12px' } : { color: T.muted }}
            onMouseEnter={e => { if (activeTab !== id) e.currentTarget.style.color = T.ink; }}
            onMouseLeave={e => { if (activeTab !== id) e.currentTarget.style.color = T.muted; }}>
            {label}
          </button>
        ))}
      </div>

      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>
      {loading && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>Loading...</p>}

      {/* Donations Tab */}
      {!loading && activeTab === 'donations' && (
        <TableCard>
          <TableBar>
            <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filteredDonations.length} donations`}</p>
            <div className="flex gap-2">
              <ActionBtn onClick={() => setShowDonationForm(!showDonationForm)} color="green">+ Record</ActionBtn>
              <ActionBtn onClick={() => handleDonationAction('APPROVE')} disabled={selectedIds.length === 0} color="gold">Approve</ActionBtn>
              <ActionBtn onClick={() => handleDonationAction('REJECT')} disabled={selectedIds.length === 0} color="muted">Reject</ActionBtn>
              <ActionBtn onClick={() => handleDonationAction('DELETE')} disabled={selectedIds.length === 0} color="red">Delete</ActionBtn>
            </div>
          </TableBar>

          {showDonationForm && (
            <div className="px-6 py-5" style={{ borderBottom: `1px solid ${T.border}`, backgroundColor: T.stripe }}>
              <p className="text-xs font-black uppercase tracking-widest mb-4" style={{ color: T.gold }}>Record Donation on Behalf</p>
              <form onSubmit={handleAdminDonationSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Email / Phone *"><input type="text" value={donationForm.donorContact} onChange={e => setDonationForm({ ...donationForm, donorContact: e.target.value })} placeholder="Email or Phone" className="vrv-input" required /></Field>
                <Field label="Donor Name"><input type="text" value={donationForm.donorName} onChange={e => setDonationForm({ ...donationForm, donorName: e.target.value })} placeholder="Optional" className="vrv-input" /></Field>
                <Field label="Amount (₹) *"><input type="number" min="1" value={donationForm.donationAmount} onChange={e => setDonationForm({ ...donationForm, donationAmount: e.target.value })} className="vrv-input" required /></Field>
                <Field label="Donation Type">
                  <select value={donationForm.donationType} onChange={e => setDonationForm({ ...donationForm, donationType: e.target.value })} className="vrv-select">
                    {donationTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
                <Field label="Donation Date"><input type="date" value={donationForm.donationDate} onChange={e => setDonationForm({ ...donationForm, donationDate: e.target.value })} className="vrv-input" /></Field>
                <div className="flex items-end gap-2">
                  <PrimaryBtn type="submit">Record →</PrimaryBtn>
                  <ActionBtn onClick={() => setShowDonationForm(false)} color="muted">Cancel</ActionBtn>
                </div>
              </form>
            </div>
          )}

          {/* Mobile cards */}
          <div className="block md:hidden divide-y" style={{ borderTop: `1px solid ${T.border}` }}>
            {filteredDonations.length === 0
              ? <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No donations found.</p>
              : filteredDonations.map((d, i) => (
                <div key={d.donationID || i} className="px-4 py-4" style={{ backgroundColor: selectedIds.includes(d.donationID) ? 'rgba(212,175,55,0.06)' : 'white' }}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={selectedIds.includes(d.donationID)} onChange={() => toggleSelect(d.donationID)} />
                      <span className="font-black text-sm" style={{ color: T.dark }}>₹{d.donationAmount}</span>
                      <span className={getStatusClass(d.status)}>{d.status}</span>
                    </div>
                    {d.reviewedBy && <button onClick={() => setViewReview(d)} className="font-black text-[10px] uppercase tracking-widest" style={{ color: T.gold }}>Review</button>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>Donor: </span>
                      {d.emailID && d.emailID.includes('@') ? <button onClick={() => { const u = getUserObj(d.emailID); if (u) setViewUserModal(u); }} className="font-bold hover:underline" style={{ color: T.gold }}>{getUserName(d.emailID) || d.donorName || '...'}</button> : <span style={{ color: T.ink }}>{d.donorName || d.emailID}</span>}
                    </div>
                    <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>Type: </span><span style={{ color: T.ink }}>{d.donationType || '—'}</span></div>
                    <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>Date: </span><span style={{ color: T.ink }}>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}</span></div>
                    <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>By: </span>
                      {d.recordedBy ? <button onClick={() => { const u = getUserObj(d.recordedBy); if (u) setViewUserModal(u); }} className="font-bold hover:underline" style={{ color: T.dark }}>{d.reviewerFullName || getUserName(d.recordedBy) || '...'}</button> : <span style={{ color: T.muted }}>—</span>}
                    </div>
                  </div>
                  <p className="font-mono text-[10px] mt-1" style={{ color: T.muted }}>{d.donationID}</p>
                </div>
              ))}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr>
                <th className="px-3 py-3 w-8" style={{ backgroundColor: T.stripe, borderBottom: `1px solid ${T.border}` }}><input type="checkbox" checked={selectedIds.length === filteredDonations.length && filteredDonations.length > 0} onChange={toggleSelectAll} /></th>
                <Th>ID</Th><Th>Donor</Th><Th>Recorded By</Th><Th>Type</Th><Th>Amount</Th><Th>Date</Th><Th>Status</Th><Th center>Review</Th>
              </tr></thead>
              <tbody>
                {filteredDonations.map((d, i) => (
                  <Tr key={d.donationID || i} selected={selectedIds.includes(d.donationID)}>
                    <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(d.donationID)} onChange={() => toggleSelect(d.donationID)} /></td>
                    <td className="px-3 py-2.5 font-mono text-[10px]" style={{ color: T.muted }}>{d.donationID}</td>
                    <td className="px-3 py-2.5">{d.emailID && d.emailID.includes('@') ? <button onClick={() => { const u = getUserObj(d.emailID); if (u) setViewUserModal(u); }} className="font-bold hover:underline" style={{ color: T.gold }}>{getUserName(d.emailID) || d.donorName || '...'}</button> : <span style={{ color: T.muted }}>{d.donorName || d.emailID}</span>}</td>
                    <td className="px-3 py-2.5">{d.recordedBy ? <button onClick={() => { const u = getUserObj(d.recordedBy); if (u) setViewUserModal(u); }} className="font-bold hover:underline" style={{ color: T.dark }}>{d.reviewerFullName || getUserName(d.recordedBy) || '...'}</button> : <span style={{ color: T.muted }}>—</span>}</td>
                    <td className="px-3 py-2.5" style={{ color: T.muted }}>{d.donationType}</td>
                    <td className="px-3 py-2.5 font-black" style={{ color: T.dark }}>₹{d.donationAmount}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5"><span className={getStatusClass(d.status)}>{d.status}</span></td>
                    <td className="px-3 py-2.5 text-center">{d.reviewedBy ? <button onClick={() => setViewReview(d)} className="font-black text-[10px] uppercase tracking-widest hover:underline" style={{ color: T.gold }}>View</button> : '—'}</td>
                  </Tr>
                ))}
              </tbody>
            </table>
            {filteredDonations.length === 0 && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No donations found.</p>}
          </div>
        </TableCard>
      )}

      {/* Expenses Tab */}
      {!loading && activeTab === 'expenses' && (
        <div>
          <div className="mb-4">
            <ActionBtn onClick={() => { setShowExpenseForm(!showExpenseForm); setEditExpense(null); setExpenseForm({ amount: '', purpose: '', paid_to: '', expense_date: '', paymentMethod: 'UPI', category: 'Other' }); }} color="dark">
              {showExpenseForm ? 'Cancel' : '+ Add Expense'}
            </ActionBtn>
          </div>

          {showExpenseForm && (
            <div className="rounded-2xl overflow-hidden mb-6" style={{ border: `1px solid ${T.border}` }}>
              <div className="px-6 py-4" style={{ borderBottom: `1px solid ${T.border}`, backgroundColor: T.stripe }}>
                <p className="text-xs font-black uppercase tracking-widest" style={{ color: T.gold }}>{editExpense ? 'Edit Expense' : 'New Expense'}</p>
              </div>
              <form onSubmit={handleExpenseSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4 px-6 py-5" style={{ background: T.bg }}>
                <Field label="Amount (₹) *"><input type="number" min="1" value={expenseForm.amount} onChange={e => setExpenseForm({ ...expenseForm, amount: e.target.value })} className="vrv-input" required /></Field>
                <Field label="Purpose *"><input type="text" value={expenseForm.purpose} onChange={e => setExpenseForm({ ...expenseForm, purpose: e.target.value })} className="vrv-input" required /></Field>
                <Field label="Paid To"><input type="text" value={expenseForm.paid_to} onChange={e => setExpenseForm({ ...expenseForm, paid_to: e.target.value })} className="vrv-input" /></Field>
                <Field label="Date"><input type="date" value={expenseForm.expense_date} onChange={e => setExpenseForm({ ...expenseForm, expense_date: e.target.value })} className="vrv-input" /></Field>
                <Field label="Payment Method">
                  <select value={expenseForm.paymentMethod} onChange={e => setExpenseForm({ ...expenseForm, paymentMethod: e.target.value })} className="vrv-select">
                    {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="Category">
                  <select value={expenseForm.category} onChange={e => setExpenseForm({ ...expenseForm, category: e.target.value })} className="vrv-select">
                    {expenseCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <div className="md:col-span-3"><PrimaryBtn type="submit">{editExpense ? 'Update Expense →' : 'Record Expense →'}</PrimaryBtn></div>
              </form>
            </div>
          )}

          <TableCard>
            {/* Mobile cards */}
            <div className="block md:hidden divide-y" style={{ borderTop: `1px solid ${T.border}` }}>
              {filteredExpenses.length === 0
                ? <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No expenses found.</p>
                : filteredExpenses.map((exp, i) => (
                  <div key={exp.expense_id || i} className="px-4 py-4" style={{ backgroundColor: 'white' }}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-bold text-sm" style={{ color: T.ink }}>{exp.purpose}</p>
                        <p className="font-black text-base" style={{ color: '#b91c1c' }}>₹{exp.amount}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={{ color: T.gold, backgroundColor: 'rgba(195,155,98,0.08)', border: '1px solid rgba(195,155,98,0.25)' }}>{exp.category || '—'}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs mb-2">
                      <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>Paid To: </span><span style={{ color: T.ink }}>{exp.paid_to || '—'}</span></div>
                      <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>Date: </span><span style={{ color: T.ink }}>{exp.expense_date || '—'}</span></div>
                      <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>Method: </span><span style={{ color: T.ink }}>{exp.paymentMethod || '—'}</span></div>
                      <div><span className="font-bold uppercase tracking-wider" style={{ color: T.muted }}>By: </span><span style={{ color: T.ink }}>{exp.createdBy || '—'}</span></div>
                    </div>
                    <div className="flex gap-2">
                      <ActionBtn onClick={() => handleEditExpense(exp)} color="gold">Edit</ActionBtn>
                      <ActionBtn onClick={async () => { const res = await expenseAction({ expense_id: exp.expense_id, action: 'DELETE' }); if (res.statusCode === 200) { showMsg('Expense deleted', 'success'); loadData(); } else showMsg('Delete failed', 'error'); }} color="red">Delete</ActionBtn>
                    </div>
                  </div>
                ))}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr><Th>ID</Th><Th>Purpose</Th><Th>Amount</Th><Th>Paid To</Th><Th>Date</Th><Th>Method</Th><Th>Category</Th><Th>Created By</Th><Th center>Actions</Th></tr></thead>
                <tbody>
                  {filteredExpenses.map((exp, i) => (
                    <Tr key={exp.expense_id || i}>
                      <td className="px-3 py-2.5 font-mono text-[10px]" style={{ color: T.muted }}>{exp.expense_id}</td>
                      <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{exp.purpose}</td>
                      <td className="px-3 py-2.5 font-black" style={{ color: '#b91c1c' }}>₹{exp.amount}</td>
                      <td className="px-3 py-2.5" style={{ color: T.muted }}>{exp.paid_to || '—'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap" style={{ color: T.muted }}>{exp.expense_date || '—'}</td>
                      <td className="px-3 py-2.5" style={{ color: T.muted }}>{exp.paymentMethod || '—'}</td>
                      <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest" style={{ color: T.gold, backgroundColor: 'rgba(195,155,98,0.08)', border: '1px solid rgba(195,155,98,0.25)' }}>{exp.category || '—'}</span></td>
                      <td className="px-3 py-2.5" style={{ color: T.muted }}>{exp.createdBy || '—'}</td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex gap-1.5 justify-center">
                          <ActionBtn onClick={() => handleEditExpense(exp)} color="gold">Edit</ActionBtn>
                          <ActionBtn onClick={async () => { const res = await expenseAction({ expense_id: exp.expense_id, action: 'DELETE' }); if (res.statusCode === 200) { showMsg('Expense deleted', 'success'); loadData(); } else showMsg('Delete failed', 'error'); }} color="red">Delete</ActionBtn>
                        </div>
                      </td>
                    </Tr>
                  ))}
                </tbody>
              </table>
              {filteredExpenses.length === 0 && <p className="text-center py-8 text-sm" style={{ color: T.muted }}>No expenses found.</p>}
            </div>
          </TableCard>
        </div>
      )}

      {viewReview && (
        <Modal onClose={() => setViewReview(null)} title="Review Details" subtitle="Review Info">
          {[['Reviewed By', viewReview.reviewerFullName || viewReview.reviewedBy], ['Reviewed At', viewReview.reviewedAt ? new Date(viewReview.reviewedAt).toLocaleString() : '—'], ['Comments', viewReview.reviewComments || 'No comments']].map(([label, val]) => (
            <div key={label} className="mb-3">
              <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: T.muted }}>{label}</p>
              <p className="text-sm px-3 py-2 rounded-lg whitespace-pre-wrap" style={{ backgroundColor: T.stripe, color: T.ink, border: `1px solid ${T.border}` }}>{val}</p>
            </div>
          ))}
          <div className="mt-4"><PrimaryBtn onClick={() => setViewReview(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}

      {viewUserModal && <Modal onClose={() => setViewUserModal(null)} title="User Details" subtitle="Member Info" wide><UserDetailFields user={viewUserModal} /><div className="mt-6"><PrimaryBtn onClick={() => setViewUserModal(null)}>Close</PrimaryBtn></div></Modal>}
      {rejectModal && <RejectModal value={rejectComment} onChange={e => setRejectComment(e.target.value)} title="Reject Donation" onCancel={() => { setRejectModal(false); setRejectComment(''); }} onConfirm={() => { handleDonationAction('REJECT', rejectComment || 'Rejected by admin'); setRejectModal(false); setRejectComment(''); }} />}
      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default TransactionsPage;
